import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import RoundButton from 'features/shared/components/buttons/RoundButton';
import DateFormatter from 'features/shared/components/formatters/DateFormatter';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import RequirementSimplifiedDTO from '../dtos/RequirementSimplifiedDTO';
import { getRequirementDetailPath, getRequirementAdminDetailPath } from '../helpers/requirementModuleNavigation';
import { EmailRequirementState } from '../enums/emailRequirementState';
import { ProcessingMode } from 'features/shared/enums/processingMode';

import styles from './requirementsimplified.module.scss';
import RenderWrapper from 'features/shared/components/renderWrapper/RenderWrapper';

interface Props {
  requirement: RequirementSimplifiedDTO;
  enableDescription?: boolean;
  forAdminUse?: boolean;
}

const RequirementSimplified = ({ requirement, enableDescription = false, forAdminUse = false }: Props) => {
  // translations
  const tTerms = useTranslation(["shared\\terms"]).t;
  const tLabels = useTranslation("requirements\\requirements").t;

  // helpers
  const requirementContentClassName = forAdminUse
    ? "col-12 col-sm-3 col-lg-6 mt-1 mt-sm-0"
    : "col-sm-6 col-lg-8";

  const requirementTypeClassName = `col-auto col-sm-12 pr-2 pr-sm-0 px-lg-3 ${
    forAdminUse
      ? `col-lg-4${enableDescription ? " col-xl-3" : ""}`
      : `col-lg-6${enableDescription ? " col-xl-4" : ""}`
  }`;

  const requirementStateClassName = `col-auto col-sm-12 pl-0 mt-sm-1 mt-lg-0 ${
    forAdminUse
      ? `col-lg-4${enableDescription ? " col-xl-3" : ""}`
      : `col-lg-6${enableDescription ? " col-xl-3" : ""}`
  }`;

  const requirementDescriptionClassName = `d-none d-xl-block pl-lg-4${
    forAdminUse ? " col-xl-4" : " col-xl-5"
  }`;

  // sub renders
  const renderState = (requirement: RequirementSimplifiedDTO): ReactElement => {
    const isEmail = requirement.processingMode === ProcessingMode.Email;

    const stateName = isEmail
      ? tLabels(`emailRequirementStates.${requirement.emailRequirementState}`)
      : requirement.actualState;

    let stateColor = "warning";

    if (isEmail) {
      if (requirement.emailRequirementState === EmailRequirementState.Rejected) {
        stateColor = "danger";
      } else if (requirement.emailRequirementState === EmailRequirementState.Completed) {
        stateColor = "success";
      }
    } else if (requirement.resolveDate) {
      stateColor = "success";
    }

    return (
      <>
        <span className={`d-inline-block rounded-circle p-1 mr-1 bg-${stateColor}`}></span>
        <small
          className={`text-${stateColor} text-truncate d-inline-block`}
          title={stateName}
        >
          {stateName}
        </small>
      </>
    );
  }
  
  const renderDescription = (description?: string) => {
    if (description) {
      return <div className={`small text-muted ${styles.descriptionItem}`}>{requirement.descriptionShort}</div>;
    } else {
      return <div className={`small text-muted ${styles.descriptionItem}`}>{tTerms("Undefined")}</div>;
    }
  }
  
  const renderResolveDate = () => {
    if (requirement.resolveDate) {
      return <DateFormatter date={requirement.resolveDate} />;
    } else {
      return <>-</>
    }
  }

  // render
  return (
    <div className="row no-gutters align-items-center">
      <div className="col">
        <div className="row no-gutters align-items-center">
          <div className="col-auto col-sm-3 col-lg-2 pr-2">
            <div className="d-flex flex-wrap flex-sm-column">
              <span className="mr-2 mr-lg-0">
                <span className="mr-1 text-muted">
                  <FontAwesomeIcon icon="hourglass-start" size="xs" fixedWidth />
                </span>
                <span className="small">
                  <DateFormatter date={requirement.issueDate!} />
                </span>
              </span>
              <span className="mt-lg-n1">
                <span className="mr-1 text-muted">
                  <FontAwesomeIcon icon="hourglass-end" size="xs" fixedWidth />
                </span>
                <span className="small">
                  {renderResolveDate()}
                </span>
              </span>
            </div>
          </div>
          <div className="col-auto col-sm-3 col-lg-2 pr-sm-2 text-truncate">
            <span className="mr-2 mr-lg-0">
              <span className="d-sm-none mr-1 text-muted">
                <FontAwesomeIcon icon="tag" size="xs" fixedWidth />
              </span>
              <span className={styles.numberItem} title={requirement.requirementNumber}>
                {requirement.requirementNumber}
              </span>
            </span>
          </div>
          <RenderWrapper isVisible={() => forAdminUse}>
            <div className="col-12 col-sm-3 col-lg-2 pr-sm-2 mt-1 mt-sm-0 text-truncate">
              <div className="text-truncate" title={requirement.customerName ?? undefined}>
                {requirement.customerName}
              </div>
            </div>
          </RenderWrapper>
          <div className={requirementContentClassName}>
            <div className="row no-gutters align-items-center">
              <RenderWrapper isVisible={() => forAdminUse}>
                <div className={`d-none d-lg-block col-lg-4${enableDescription ? " col-xl-2" : ""}`}>
                  <div className="text-truncate" title={requirement.requirementCategory?.name}>
                    {requirement.requirementCategory?.name}
                  </div>
                </div>
              </RenderWrapper>
              <div className={requirementTypeClassName}> 
                <div className="text-truncate" title={requirement.requirementType?.name}>
                  {requirement.requirementType?.name}
                </div>
              </div>
              <div className={requirementStateClassName}>
                <div className="d-flex align-items-center text-truncate">
                  {renderState(requirement)}
                </div>
              </div>
              <RenderWrapper isVisible={() => enableDescription}>
                <div className={requirementDescriptionClassName}>
                  {renderDescription(requirement.descriptionShort)}
                </div>
              </RenderWrapper>
            </div>
          </div>
        </div>
      </div>
      <div className="col-auto pl-2 pl-lg-3">
        <RoundButton
          asLink
          to={
            forAdminUse
              ? getRequirementAdminDetailPath(requirement.requirementId!)
              : getRequirementDetailPath(requirement.requirementId!)
          }
        />
      </div>
    </div>
  )
}

export default RequirementSimplified;